/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { preferencesService } from '../../services/api';
import toast from 'react-hot-toast';
import { Button, Skeleton } from '../../components/console/ui';
import type { AppearancePreferences } from '../../types';
import {
  APPEARANCE_PREFS_QUERY_KEY,
  DEFAULT_APPEARANCE_PREFS,
  fetchAppearancePreferences,
} from '../../hooks/usePriceUnit';

type Theme = AppearancePreferences['theme'];
type ToggleKey = 'compact_mode' | 'sidebar_collapsed' | 'show_prices_per_minute';

interface ThemeOption {
  id: Theme;
  name: string;
  preview: React.ReactNode;
}

const PreviewPane: React.FC<{ bg: string; side: string; main: string }> = ({ bg, side, main }) => (
  <div className="h-24 rounded-md border border-ps-strong flex gap-1.5 p-2" style={{ background: bg }} aria-hidden="true">
    <span className="w-[28%] rounded" style={{ background: side }} />
    <span className="flex-1 rounded" style={{ background: main }} />
  </div>
);

const themeOptions: ThemeOption[] = [
  { id: 'dark', name: 'Dark', preview: <PreviewPane bg="#050508" side="#0D0D13" main="#1A1A24" /> },
  { id: 'light', name: 'Light', preview: <PreviewPane bg="#F6F6F4" side="#E6E6E2" main="#FFFFFF" /> },
  {
    id: 'system',
    name: 'Match system',
    preview: (
      <div
        className="h-24 rounded-md border border-ps-strong"
        style={{ background: 'linear-gradient(90deg,#050508 50%,#F6F6F4 50%)' }}
        aria-hidden="true"
      />
    ),
  },
];

const AppearanceSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const [preferences, setPreferences] = useState<AppearancePreferences>(DEFAULT_APPEARANCE_PREFS);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch appearance preferences - gracefully handle if API not available
  const { data: fetchedPreferences, isLoading } = useQuery({
    queryKey: APPEARANCE_PREFS_QUERY_KEY,
    queryFn: fetchAppearancePreferences,
    retry: false,
  });

  // Update local state when data is fetched
  useEffect(() => {
    if (fetchedPreferences) {
      setPreferences(fetchedPreferences);
      setHasChanges(false);
    }
  }, [fetchedPreferences]);

  // Update preferences mutation
  const updateMutation = useMutation({
    mutationFn: (data: AppearancePreferences) => preferencesService.updateAppearance(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APPEARANCE_PREFS_QUERY_KEY });
      setHasChanges(false);
      toast.success('Appearance settings saved');
    },
    onError: () => {
      toast.error('Failed to save appearance settings');
    },
  });

  const handleThemeChange = (theme: Theme) => {
    // Currently only dark theme is fully supported
    if (theme !== 'dark') {
      toast('Light theme coming soon! Dark theme is currently the default.');
    }
    setPreferences((prev) => ({ ...prev, theme }));
    setHasChanges(true);
  };

  const handleToggle = (key: ToggleKey) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate(preferences);
  };

  if (isLoading) {
    return <Skeleton className="h-[360px] rounded-[14px]" />;
  }

  const rowCls = 'flex justify-between items-center gap-3 py-3.5 border-t border-ps-raised cursor-pointer';
  const checkboxCls = 'w-[18px] h-[18px] accent-[#F97316] cursor-pointer flex-none';

  return (
    <>
      <section className="ps-card p-5 sm:p-6 flex flex-col gap-[18px]" aria-labelledby="theme-heading">
        <h2 id="theme-heading" className="m-0 text-lg font-medium text-ps-text">
          Theme
        </h2>
        <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,180px),1fr))]" role="radiogroup" aria-labelledby="theme-heading">
          {themeOptions.map((option) => {
            const on = preferences.theme === option.id;
            return (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => handleThemeChange(option.id)}
                className={`flex flex-col gap-2.5 p-3 rounded-[14px] border text-left text-sm font-medium text-ps-text transition-colors ${
                  on ? 'border-ps-accent bg-[#1A0F08]' : 'border-ps-strong bg-ps-card hover:border-ps-muted'
                }`}
              >
                {option.preview}
                {option.name}
              </button>
            );
          })}
        </div>
        <p className="m-0 -mt-1.5 text-ps-subtle text-xs">The console currently renders in dark. Your choice is saved for when light mode ships.</p>
        <div>
          <label className={rowCls}>
            <div>
              <div className="font-medium text-ps-text">Compact tables</div>
              <div className="text-ps-subtle text-[13px]">Fit more rows on pods, transactions and logs</div>
            </div>
            <input
              type="checkbox"
              className={checkboxCls}
              checked={!!preferences.compact_mode}
              onChange={() => handleToggle('compact_mode')}
              aria-label="Compact tables"
            />
          </label>
          <label className={rowCls}>
            <div>
              <div className="font-medium text-ps-text">Show prices per minute</div>
              <div className="text-ps-subtle text-[13px]">Alongside hourly prices in the GPU lists</div>
            </div>
            <input
              type="checkbox"
              className={checkboxCls}
              checked={!!preferences.show_prices_per_minute}
              onChange={() => handleToggle('show_prices_per_minute')}
              aria-label="Show prices per minute"
            />
          </label>
          <label className={rowCls}>
            <div>
              <div className="font-medium text-ps-text">Remember collapsed sections</div>
              <div className="text-ps-subtle text-[13px]">Keep sidebar sections collapsed as you left them</div>
            </div>
            <input
              type="checkbox"
              className={checkboxCls}
              checked={!!preferences.sidebar_collapsed}
              onChange={() => handleToggle('sidebar_collapsed')}
              aria-label="Remember collapsed sections"
            />
          </label>
        </div>
      </section>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={updateMutation.isPending || !hasChanges}>
          {updateMutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </>
  );
};

export default AppearanceSettings;
